import { createNamespacedHelpers } from 'vuex-composition-helpers'

import { Action, ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex'
import ajax from '../../../modules/ajax'
import { Models } from '../../../types'
import { computed, ComputedRef, Ref, ref, UnwrapRef } from '@vue/composition-api'

type Method = 'index'|'create'|'update'|'delete'
interface Resource {
  modelId?: number
  name?: string
}
export declare interface CrudState<ModelType> {
    all: ModelType[]
    resource: Resource
}

export declare type CrudGetters<ModelType> = GetterTree<CrudState<ModelType>, unknown>
export declare type CrudMutations<ModelType> = MutationTree<CrudState<ModelType>>

export declare interface CrudActions<ModelType> extends ActionTree<CrudState<ModelType>, unknown> {
  setResource: (context: ActionContext<CrudState<ModelType>, unknown>, resource: Resource) => void
  fetch: (context: ActionContext<CrudState<ModelType>, unknown>, payload: unknown) => Promise<void>
  create: (context: ActionContext<CrudState<ModelType>, unknown>, payload: ModelType) => Promise<void>
  update: (context: ActionContext<CrudState<ModelType>, unknown>, payload: ModelType) => Promise<void>
  destroy: (context: ActionContext<CrudState<ModelType>, unknown>, payload: ModelType) => Promise<void>
  save: (context: ActionContext<CrudState<ModelType>, unknown>, payload: ModelType) => Promise<void>
}

export const crudNamespaced = <T>(namespace: string) => {
  return createNamespacedHelpers<CrudState<T>, CrudGetters<T>, CrudActions<T>, CrudMutations<T>>(namespace)
}

export declare type SaveFunction<T> = (payload: T) => void
export declare type DestroyFunction<T> = (payload: T) => void
export declare type ValidatorFunction<T> = (item: T) => boolean

export const defaultCrud = <T extends Models.BaseModel, V = Partial<T>>(namespace: string, validatorFunction?: ValidatorFunction<T>, newItem?: V) => {
  const { useState, useActions } = crudNamespaced<T>(namespace)
  const { fetch, save, destroy, setResource } = useActions([
    'fetch',
    'save',
    'destroy',
    'setResource'
  ])
  const { all } = useState(['all'])

  const {
    select,
    selected,
    unselect,
    saveSelected,
    destroyItem,
    addNew,
    isValid
  } = useCrudHelpers<T, V>(
    save,
    destroy,
    validatorFunction,
    newItem
  )

  return {
    setResource,
    fetch,
    all,
    select,
    selected,
    unselect,
    saveSelected,
    destroyItem,
    addNew,
    isValid
  }
}

export const useCrudHelpers = <T extends Models.BaseModel, V>(
  saveFunction: SaveFunction<T>,
  destroyFunction: DestroyFunction<T>,
  validatorFunction?: ValidatorFunction<T>,
  newValue?: V
): {
  select: (item: T) => void,
  unselect: () => void,
  selected: Ref<UnwrapRef<T> | undefined>,
  saveSelected: () => void,
  destroyItem: (item: T) => void,
  addNew: () => void,
  isValid: ComputedRef<boolean>
} => {
  function select (item: T) {
    selected.value = { ...item } as UnwrapRef<T>
  }

  function unselect () {
    selected.value = undefined
  }

  function addNew () {
    selected.value = {
      id: 0,
      ...newValue
    } as UnwrapRef<T>
  }

  function saveSelected () {
    if (selected.value) {
      if (isValid) {
        saveFunction(selected.value as T)
        unselect()
      }
    }
  }

  function destroyItem (item: T) {
    destroyFunction(item)
    if (selected.value && item.id === selected.value.id) {
      unselect()
    }
  }

  const selected = ref<T|undefined>(undefined)

  const isValid = computed(() => {
    if (validatorFunction === undefined) {
      return true
    }

    return selected.value !== undefined ? validatorFunction(selected.value as T) : false
  })

  return {
    select,
    unselect,
    selected,
    saveSelected,
    destroyItem,
    addNew,
    isValid
  }
}

export default class <ModelType extends Models.BaseModel> {
  public state: CrudState<ModelType>
  public getters: CrudGetters<ModelType>
  public actions: CrudActions<ModelType>
  public mutations: CrudMutations<ModelType>
  private methodMaps: Record<Method, string>

  constructor (resourceName?: string) {
    this.methodMaps = {
      index: 'index',
      create: 'create',
      update: 'update',
      delete: 'delete'
    }

    this.state = {
      all: [],
      resource: {
        modelId: undefined,
        name: resourceName
      }
    }

    this.getters = {
    }

    this.actions = {
      setResource: (context, resource) => {
        context.commit('SET_RESOURCE', resource)
      },

      fetch: async (context, payload) => {
        const response = await ajax.get<ModelType[]>(this.getUrl(context, 'index'), payload)
        context.commit('ALL', response.data)
      },

      create: async (context, payload) => {
        const result = await ajax.post<ModelType>(this.getUrl(context, 'create'), payload)
        if (!result.error) {
          context.commit('CREATE', result.data)
        }
      },

      update: async (context, payload) => {
        const result = await ajax.post<ModelType>(this.getUrl(context, 'update'), payload)
        if (!result.error) {
          context.commit('UPDATE', result.data)
        }
      },

      save: (context, payload) => {
        if (payload.id === 0) {
          return context.dispatch('create', payload)
        }

        return context.dispatch('update', payload)
      },

      destroy: async (context, payload) => {
        const result = await ajax.get(this.getUrl(context, 'delete', payload.id), payload)
        if (!result.error) {
          context.commit('DELETE', payload)
        }
      }
    }

    this.mutations = {
      SET_RESOURCE: (state, resource: Resource): void => {
        state.resource = resource
      },

      ALL: (state, all: ModelType[]): void => {
        state.all = all
      },

      CREATE: (state, item: ModelType): void => {
        state.all.push(item)
      },

      UPDATE: (state, newItem: ModelType): void => {
        state.all = state.all.map(originalItem => {
          if (originalItem.id === newItem.id) {
            return newItem
          }

          return originalItem
        })
      },

      DELETE: (state, item: ModelType): void => {
        state.all = state.all.filter(el => el.id !== item.id)
      }
    }
  }

  addAction (name: string, action: Action<CrudState<ModelType>, unknown>): this {
    this.actions[name] = action

    return this
  }

  export (): Record<string, unknown> {
    return {
      mutations: this.mutations,
      actions: this.actions,
      getters: this.getters,
      state: this.state
    }
  }

  mapMethod (method: Method, value: string): this {
    this.methodMaps[method] = value

    return this
  }

  getUrl (context: ActionContext<CrudState<ModelType>, unknown>, method: Method, itemId?: number): string {
    const base = [`/${context.state.resource.name}`]

    if (context.state.resource.modelId) {
      base.push(context.state.resource.modelId.toString())
    }

    base.push(this.methodMaps[method])

    if (itemId) {
      base.push(itemId.toString())
    }

    return base.join('/')
  }
}
